import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL} from './constants';
import { getToken } from '../../services/tokenService';
export default function useParametresBaticoLogic() {
  const [parametresActifs, setParametresActifs] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedParams, setSelectedParams] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewParams, setPreviewParams] = useState(null);
  const [importData, setImportData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    coefficients: true,
    remises: true,
    seuil: true,
    custom: true,
    restrictions: true
  });
  const fileInputRef = useRef(null);
  
  // État pour les paramètres personnalisés
  const [customParams, setCustomParams] = useState([]);
  const [customParamDialogOpen, setCustomParamDialogOpen] = useState(false);
  const [editingCustomParam, setEditingCustomParam] = useState(null);
  const [customParamForm, setCustomParamForm] = useState({
    nom: '',
    type: 'taxe',
    mode_calcul: 'pourcentage',
    valeur: 0,
    description: '',
    applicable_a: ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'],
    is_active: true
  });

  // État pour les restrictions
  const [restrictions, setRestrictions] = useState([]);
  const [restrictionDialogOpen, setRestrictionDialogOpen] = useState(false);
  const [editingRestriction, setEditingRestriction] = useState(null);
  const [restrictionForm, setRestrictionForm] = useState({
    nom: '',
    type: 'territoire',
    condition: 'equals',
    valeur: '',
    territoires_selectionnes: [], // Nouveau: liste des territoires sélectionnés
    action: 'bloquer',
    action_config: { pourcentage: 0 },
    message: '',
    message_interne: '',
    is_active: true,
    priorite: 0,
    applicable_categories: ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'],
    applicable_modes: ['annuel', 'nominative']
  });
  
  // Liste des territoires disponibles
  const [territoiresDisponibles, setTerritoiresDisponibles] = useState([]);

  // État pour les ajustements de montants (Super Admin)
  const [ajustementsMontants, setAjustementsMontants] = useState([]);
  const [ajustementDialogOpen, setAjustementDialogOpen] = useState(false);
  const [editingAjustement, setEditingAjustement] = useState(null);
  const [ajustementForm, setAjustementForm] = useState({
    nom: '',
    description: '',
    type_ajustement: 'remplacement',
    cible: 'tous',
    valeur: 0,
    valeur_remplacement_ht: '',
    valeur_remplacement_ttc: '',
    valeur_remplacement_ca: '',
    applicable_modes: ['annuel', 'nominative'],
    applicable_categories: ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'],
    priorite: 0,
    is_active: true,
    afficher_sur_documents: true,
    libelle_document: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    coef_gros_oeuvre: 0.002945,
    coef_second_oeuvre: 0.002695,
    coef_intellectuels: 0.0045,
    coef_travaux_publics: 0.00425,
    remise_intellectuels: 0.0,
    remise_gros_oeuvre: 0.0,
    remise_second_oeuvre: 0.0,
    remise_travaux_publics: 0.0,
    seuil_ca: 40000.0,
    seuil_ca_actif: false
  });

  // Statistics
  const [stats, setStats] = useState({
    totalParams: 0,
    activeParams: 0,
    archivedParams: 0,
    lastUpdate: null,
    avgRemise: 0
  });

  useEffect(() => {
    fetchData();
    fetchCustomParams();
    fetchTerritoires();
    fetchAjustementsMontants();
  }, []);
  
  // Charger la liste des territoires
  const fetchTerritoires = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/territoires`);
      setTerritoiresDisponibles(response.data || []);
    } catch {
    }
  };

  // Charger les ajustements de montants
  const fetchAjustementsMontants = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/batico/parametres-batico/ajustements-montants`, { headers });
      setAjustementsMontants(response.data || []);
    } catch {
    }
  };

  // Créer un ajustement de montant
  const handleCreateAjustement = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(
        `${BACKEND_URL}/api/batico/parametres-batico/ajustements-montants`,
        ajustementForm,
        { headers }
      );
      
      toast.success('Ajustement créé avec succès');
      setAjustementDialogOpen(false);
      resetAjustementForm();
      fetchAjustementsMontants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  // Mettre à jour un ajustement
  const handleUpdateAjustement = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(
        `${BACKEND_URL}/api/batico/parametres-batico/ajustements-montants/${editingAjustement.id}`,
        ajustementForm,
        { headers }
      );
      
      toast.success('Ajustement mis à jour avec succès');
      setAjustementDialogOpen(false);
      setEditingAjustement(null);
      resetAjustementForm();
      fetchAjustementsMontants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  // Supprimer un ajustement
  const handleDeleteAjustement = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet ajustement ?')) return;
    
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(
        `${BACKEND_URL}/api/batico/parametres-batico/ajustements-montants/${id}`,
        { headers }
      );
      
      toast.success('Ajustement supprimé avec succès');
      fetchAjustementsMontants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Ouvrir le dialogue d'édition
  const openEditAjustement = (ajustement) => {
    setEditingAjustement(ajustement);
    setAjustementForm({
      nom: ajustement.nom || '',
      description: ajustement.description || '',
      type_ajustement: ajustement.type_ajustement || 'remplacement',
      cible: ajustement.cible || 'tous',
      valeur: ajustement.valeur || 0,
      valeur_remplacement_ht: ajustement.valeur_remplacement_ht || '',
      valeur_remplacement_ttc: ajustement.valeur_remplacement_ttc || '',
      valeur_remplacement_ca: ajustement.valeur_remplacement_ca || '',
      applicable_modes: ajustement.applicable_modes || ['annuel', 'nominative'],
      applicable_categories: ajustement.applicable_categories || ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'],
      priorite: ajustement.priorite || 0,
      is_active: ajustement.is_active !== false,
      afficher_sur_documents: ajustement.afficher_sur_documents !== false,
      libelle_document: ajustement.libelle_document || ''
    });
    setAjustementDialogOpen(true);
  };

  // Reset le formulaire
  const resetAjustementForm = () => {
    setAjustementForm({
      nom: '',
      description: '',
      type_ajustement: 'remplacement',
      cible: 'tous',
      valeur: 0,
      valeur_remplacement_ht: '',
      valeur_remplacement_ttc: '',
      valeur_remplacement_ca: '',
      applicable_modes: ['annuel', 'nominative'],
      applicable_categories: ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'],
      priorite: 0,
      is_active: true,
      afficher_sur_documents: true,
      libelle_document: ''
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [activeResponse, historiqueResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/parametres-batico`),
        axios.get(`${BACKEND_URL}/api/parametres-batico/historique`, { headers })
      ]);

      setParametresActifs(activeResponse.data);
      setHistorique(historiqueResponse.data);

      // Calculate stats
      const active = historiqueResponse.data.filter(p => p.is_active && !p.is_archived).length;
      const archived = historiqueResponse.data.filter(p => p.is_archived).length;
      const lastUpdated = historiqueResponse.data.length > 0 
        ? historiqueResponse.data.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0].updated_at
        : null;
      
      // Calculate average remise
      const totalRemises = activeResponse.data 
        ? (activeResponse.data.remise_gros_oeuvre + activeResponse.data.remise_second_oeuvre + 
           activeResponse.data.remise_intellectuels + activeResponse.data.remise_travaux_publics) / 4
        : 0;

      setStats({
        totalParams: historiqueResponse.data.length,
        activeParams: active,
        archivedParams: archived,
        lastUpdate: lastUpdated,
        avgRemise: totalRemises
      });

    } catch {
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseFloat(value) || 0
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      if (editMode && selectedParams) {
        await axios.put(
          `${BACKEND_URL}/api/parametres-batico/${selectedParams.id}`,
          formData,
          { headers }
        );
        toast.success('Paramètres mis à jour avec succès');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/parametres-batico`,
          formData,
          { headers }
        );
        toast.success('Nouveaux paramètres créés avec succès');
      }

      setDialogOpen(false);
      setEditMode(false);
      setSelectedParams(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (params) => {
    setFormData({
      coef_gros_oeuvre: params.coef_gros_oeuvre,
      coef_second_oeuvre: params.coef_second_oeuvre,
      coef_intellectuels: params.coef_intellectuels,
      coef_travaux_publics: params.coef_travaux_publics,
      remise_intellectuels: params.remise_intellectuels,
      remise_gros_oeuvre: params.remise_gros_oeuvre,
      remise_second_oeuvre: params.remise_second_oeuvre,
      remise_travaux_publics: params.remise_travaux_publics,
      seuil_ca: params.seuil_ca,
      seuil_ca_actif: params.seuil_ca_actif
    });
    setSelectedParams(params);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleArchive = async (paramId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver ces paramètres ?')) {
      return;
    }

    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(
        `${BACKEND_URL}/api/parametres-batico/${paramId}/archiver`,
        {},
        { headers }
      );

      toast.success('Paramètres archivés avec succès');
      fetchData();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleDelete = async (paramId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir SUPPRIMER définitivement ces paramètres ? Cette action est irréversible.')) {
      return;
    }

    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(
        `${BACKEND_URL}/api/parametres-batico/${paramId}`,
        { headers }
      );

      toast.success('Paramètres supprimés avec succès');
      fetchData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openNewDialog = () => {
    setFormData({
      coef_gros_oeuvre: 0.002945,
      coef_second_oeuvre: 0.002695,
      coef_intellectuels: 0.0045,
      coef_travaux_publics: 0.00425,
      remise_intellectuels: 0.0,
      remise_gros_oeuvre: 0.0,
      remise_second_oeuvre: 0.0,
      remise_travaux_publics: 0.0,
      seuil_ca: 40000.0,
      seuil_ca_actif: false
    });
    setEditMode(false);
    setSelectedParams(null);
    setDialogOpen(true);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Export functions
  const exportToJSON = () => {
    const dataToExport = {
      parametres_actifs: parametresActifs,
      historique: historique,
      exported_at: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `parametres_batico_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export JSON réussi !');
  };

  const exportToCSV = () => {
    const headers = [
      'ID', 'Date Création', 'Coef Gros Oeuvre', 'Coef Second Oeuvre', 
      'Coef Intellectuels', 'Coef Travaux Publics', 'Remise GO', 'Remise 2nd',
      'Remise PIB', 'Remise TP', 'Seuil CA', 'Seuil Actif', 'Actif', 'Archivé'
    ];
    
    const rows = historique.map(p => [
      p.id,
      new Date(p.created_at).toLocaleDateString('fr-FR'),
      p.coef_gros_oeuvre,
      p.coef_second_oeuvre,
      p.coef_intellectuels,
      p.coef_travaux_publics,
      (p.remise_gros_oeuvre * 100).toFixed(2) + '%',
      (p.remise_second_oeuvre * 100).toFixed(2) + '%',
      (p.remise_intellectuels * 100).toFixed(2) + '%',
      (p.remise_travaux_publics * 100).toFixed(2) + '%',
      p.seuil_ca,
      p.seuil_ca_actif ? 'Oui' : 'Non',
      p.is_active ? 'Oui' : 'Non',
      p.is_archived ? 'Oui' : 'Non'
    ]);

    const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `parametres_batico_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export CSV réussi !');
  };

  const exportToExcel = () => {
    // Using CSV format with .xlsx extension for Excel compatibility
    const headers = [
      'ID', 'Date Création', 'Coef Gros Oeuvre', 'Coef Second Oeuvre', 
      'Coef Intellectuels', 'Coef Travaux Publics', 'Remise GO (%)', 'Remise 2nd (%)',
      'Remise PIB (%)', 'Remise TP (%)', 'Seuil CA (€)', 'Seuil Actif', 'Actif', 'Archivé'
    ];
    
    const rows = historique.map(p => [
      p.id,
      new Date(p.created_at).toLocaleDateString('fr-FR'),
      p.coef_gros_oeuvre,
      p.coef_second_oeuvre,
      p.coef_intellectuels,
      p.coef_travaux_publics,
      (p.remise_gros_oeuvre * 100).toFixed(2),
      (p.remise_second_oeuvre * 100).toFixed(2),
      (p.remise_intellectuels * 100).toFixed(2),
      (p.remise_travaux_publics * 100).toFixed(2),
      p.seuil_ca,
      p.seuil_ca_actif ? 'Oui' : 'Non',
      p.is_active ? 'Oui' : 'Non',
      p.is_archived ? 'Oui' : 'Non'
    ]);

    const csvContent = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `parametres_batico_${new Date().toISOString().split('T')[0]}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export Excel réussi !');
  };

  // ========================================
  // PARAMÈTRES PERSONNALISÉS - CRUD
  // ========================================
  
  const fetchCustomParams = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/parametres-batico/custom`, { headers });
      setCustomParams(response.data || []);
    } catch {
      // Initialize with empty array if endpoint doesn't exist yet
      setCustomParams([]);
    }
  };

  return {
    ajustementDialogOpen, ajustementForm, ajustementsMontants, customParamDialogOpen, customParamForm, customParams,
    dialogOpen, editMode, editingAjustement, editingCustomParam, editingRestriction, expandedSections,
    exportToCSV, exportToExcel, exportToJSON, fetchAjustementsMontants, fetchCustomParams, fetchData,
    fetchTerritoires, fileInputRef, formData, handleArchive, handleCreateAjustement, handleDelete,
    handleDeleteAjustement, handleEdit, handleInputChange, handleSubmit, handleUpdateAjustement, historique,
    importData, importDialogOpen, loading, openEditAjustement, openNewDialog, parametresActifs,
    previewDialogOpen, previewParams, resetAjustementForm, restrictionDialogOpen, restrictionForm, restrictions,
    selectedParams, setAjustementDialogOpen, setAjustementForm, setAjustementsMontants, setCustomParamDialogOpen, setCustomParamForm,
    setCustomParams, setDialogOpen, setEditMode, setEditingAjustement, setEditingCustomParam, setEditingRestriction,
    setExpandedSections, setFormData, setHistorique, setImportData, setImportDialogOpen, setLoading,
    setParametresActifs, setPreviewDialogOpen, setPreviewParams, setRestrictionDialogOpen, setRestrictionForm, setRestrictions,
    setSelectedParams, setStats, setTerritoiresDisponibles, stats, territoiresDisponibles, toggleSection};
}
